<template>
  <basic-page
    title="Kauppalistapohjat"
    :prev="true"
    :fallback="{ name: 'Muonitus' }"
  >
    <v-row class="fill-height">
      <v-col cols="12" lg="8" xl="8" offset-xl="2" offset-lg="2">
        <v-row>
          <v-col>
            <v-btn color="primary" @click="createTemplate"
              >Uusi kauppalistapohja</v-btn
            >
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="template in templates"
            :key="template.id"
            cols="12"
            md="6"
          >
            <v-card>
              <v-list-item>
                <v-list-item-content>
                  <div class="text-overline mb-4">Kauppalistapohja</div>
                  <v-list-item-subtitle
                    :class="$vuetify.theme.dark ? 'white--text' : 'black--text'"
                    class="text-h6 mb-1 text-wrap"
                  >
                    {{ template.name }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-wrap">
                    {{ template.items.length }} tuotetta
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-avatar>
                  <v-icon large>mdi-file-document-outline</v-icon>
                </v-list-item-avatar>
              </v-list-item>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn icon @click="editTemplate(template)"
                  ><v-icon>mdi-pencil</v-icon></v-btn
                >
                <v-btn icon color="red" @click="deleteTemplate(template)"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Edit Dialog -->
    <v-dialog v-model="dialog" max-width="800px">
      <v-card>
        <v-card-title>Muokkaa kauppalistapohjaa</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-text-field
            v-model="editedTemplate.name"
            label="Kauppalistapohjan nimi"
          ></v-text-field>
          <v-divider class="my-3"></v-divider>
          <div
            v-for="(item, index) in editedTemplate.items"
            :key="index"
            class="mb-4 pa-2 rounded"
            style="border: 1px solid #eee"
          >
            <v-row dense>
              <v-col cols="12" sm="6" class="mb-2 mb-sm-0">
                <v-text-field
                  v-model="item.name"
                  label="Tuote"
                  hide-details
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="10" sm="5">
                <v-select
                  v-model="item.category"
                  :items="categories"
                  label="Kategoria"
                  hide-details
                  dense
                ></v-select>
              </v-col>
              <v-col cols="2" sm="1" class="d-flex justify-end">
                <v-btn icon color="red" @click="removeItem(index)"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </v-col>
            </v-row>
            <v-row dense class="align-center">
              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.factor"
                  label="Määrä"
                  type="number"
                  hide-details
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="2" sm="1">
                <v-btn
                  icon
                  small
                  @click="openCalculator(item)"
                  title="Laske kerroin"
                  ><v-icon small>mdi-calculator</v-icon></v-btn
                >
              </v-col>
              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="item.unit"
                  label="Yksikkö"
                  hide-details
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-checkbox
                  v-model="item.fixed"
                  label="Kiinteä"
                  hide-details
                  dense
                  class="mt-0"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="item.comment"
                  label="Kommentti (esim. pakkauskoko)"
                  hide-details
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense class="align-center">
              <v-col cols="12">
                <div class="d-flex align-center">
                  <v-avatar v-if="item.imageUrl" size="32" tile class="mr-2">
                    <img :src="item.imageUrl" />
                  </v-avatar>
                  <v-file-input
                    label="Kuva"
                    dense
                    hide-details
                    prepend-icon="mdi-camera"
                    accept="image/*"
                    @change="(file) => uploadImage(item, file)"
                  ></v-file-input>
                </div>
              </v-col>
            </v-row>
          </div>
          <v-btn text color="primary" @click="addItem" class="mt-2"
            >Lisää tuote</v-btn
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Peruuta</v-btn>
          <v-btn color="primary" @click="saveTemplate">Tallenna</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Calculator Dialog -->
    <v-dialog v-model="calculatorDialog" max-width="300">
      <v-card>
        <v-card-title>Laske kerroin</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-text-field
            v-model.number="calcAmount"
            label="Määrä (esim. 1 pkt)"
          ></v-text-field>
          <v-text-field
            v-model.number="calcPeople"
            label="Riittää (esim. 15 hlö)"
          ></v-text-field>
          <div v-if="calcPeople > 0" class="mt-2">
            Tulos: {{ (calcAmount / calcPeople).toFixed(4) }} / hlö
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="calculatorDialog = false">Peruuta</v-btn>
          <v-btn color="primary" @click="applyCalculation">Käytä</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </basic-page>
</template>

<script>
import { firestore as db, storage } from "@/plugins/firebase.app";
import {
  collection,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import BasicPage from "@/components/BasicPage.vue";

export default {
  name: "MuonitusPohjat",
  components: { BasicPage },
  data() {
    return {
      templates: [],
      dialog: false,
      editedTemplate: {
        name: "",
        items: [],
      },
      categories: [],
      unsubscribe: null,
      calculatorDialog: false,
      calcAmount: 1,
      calcPeople: 1,
      currentItem: null,
    };
  },
  async created() {
    // Fetch settings
    const settingsRef = doc(db, "catering_settings", "general");
    const settingsSnap = await getDoc(settingsRef);
    if (settingsSnap.exists()) {
      const data = settingsSnap.data();
      this.categories = data.categories || [];
    } else {
      this.categories = [
        "HeVi",
        "Leipä",
        "Kuivatuotteet",
        "Maitotuotteet",
        "Juomat",
        "Tarvikkeet",
        "Muu",
      ];
    }

    this.unsubscribe = onSnapshot(
      collection(db, "catering_templates"),
      (snapshot) => {
        this.templates = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      }
    );
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    createTemplate() {
      this.editedTemplate = { name: "Uusi kauppalistapohja", items: [] };
      this.dialog = true;
    },
    editTemplate(template) {
      this.editedTemplate = JSON.parse(JSON.stringify(template)); // Deep copy
      this.dialog = true;
    },
    async deleteTemplate(template) {
      if (
        await this.$confirm("Haluatko varmasti poistaa kauppalistapohjan?", {
          title: "Poista kauppalistapohja",
          color: "error",
          buttonTrueText: "Poista",
        })
      ) {
        await deleteDoc(doc(db, "catering_templates", template.id));
        this.$toast("Kauppalistapohja poistettu", { type: "success" });
      }
    },
    addItem() {
      this.editedTemplate.items.push({
        name: "",
        factor: 1,
        unit: "kpl",
        fixed: false,
        category: "Muu",
      });
    },
    async removeItem(index) {
      if (
        await this.$confirm("Haluatko varmasti poistaa tuotteen?", {
          title: "Poista tuote",
          color: "error",
          buttonTrueText: "Poista",
        })
      ) {
        this.editedTemplate.items.splice(index, 1);
      }
    },
    async saveTemplate() {
      if (this.editedTemplate.id) {
        const ref = doc(db, "catering_templates", this.editedTemplate.id);
        const data = { ...this.editedTemplate };
        delete data.id;
        await updateDoc(ref, data);
      } else {
        await addDoc(collection(db, "catering_templates"), this.editedTemplate);
      }
      this.dialog = false;
    },
    openCalculator(item) {
      this.currentItem = item;
      this.calcAmount = 1;
      this.calcPeople = 15;
      this.calculatorDialog = true;
    },
    applyCalculation() {
      if (this.currentItem && this.calcPeople > 0) {
        this.currentItem.factor = parseFloat(
          (this.calcAmount / this.calcPeople).toFixed(4)
        );
      }
      this.calculatorDialog = false;
    },
    async uploadImage(item, file) {
      if (!file) return;
      try {
        const storageRef = ref(
          storage,
          `shopping-list-images/${Date.now()}_${file.name}`
        );
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        // Vue 2 reactivity caveat: adding new property to object
        this.$set(item, "imageUrl", url);
        this.$toast("Kuva ladattu", { type: "success" });
      } catch (e) {
        console.error(e);
        this.$toast("Virhe kuvan latauksessa", { type: "error" });
      }
    },
  },
};
</script>
